import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Target, Users, Zap, Heart, Eye, ArrowRight, MapPin, Menu, X } from 'lucide-react';

const AboutPage: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const styles = {
    container: { maxWidth: '1200px', margin: '0 auto', padding: '0 24px' },
    nav: { borderBottom: '1px solid #e8eaed', padding: '16px 0', position: 'sticky' as const, top: 0, zIndex: 1000, backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.95)' },
    navContainer: { maxWidth: '1200px', margin: '0 auto', padding: '0 16px', display: 'flex', justifyContent: isMobile ? 'flex-start' : 'space-between', alignItems: 'center', gap: isMobile ? '12px' : '0' },
    logo: { fontSize: isMobile ? '20px' : '24px', fontWeight: 700, color: '#14B8A6', textDecoration: 'none', letterSpacing: '-0.5px' },
    navLinks: { display: 'flex', gap: '32px', alignItems: 'center' },
    mobileMenu: { position: 'fixed' as const, top: 0, left: 0, width: '80%', maxWidth: '400px', height: '100vh', background: '#ffffff', zIndex: 2000, padding: '80px 24px 24px', display: 'flex', flexDirection: 'column' as const, gap: '24px' },
    mobileMenuOverlay: { position: 'fixed' as const, top: 0, left: 0, width: '100%', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 1999 },
    hamburger: { background: 'none', border: 'none', cursor: 'pointer', padding: '8px' },
    closeButton: { position: 'absolute' as const, top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', padding: '8px' },
    navLink: { color: '#5f6368', textDecoration: 'none', fontSize: '14px', fontWeight: 500, transition: 'color 0.2s' },
    primaryButton: { background: '#14B8A6', color: '#ffffff', padding: '12px 24px', borderRadius: '24px', textDecoration: 'none', fontSize: '14px', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.2s', display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)' },
  };

  const values = [
    { icon: <Zap size={32} color="#14B8A6" />, title: 'Flexibility', desc: 'Freedom to choose — no commitments, no lock-ins. Fitness on your terms.' },
    { icon: <Eye size={32} color="#14B8A6" />, title: 'Transparency', desc: 'No hidden costs, no surprises. What you see is what you pay.' },
    { icon: <Users size={32} color="#14B8A6" />, title: 'Inclusivity', desc: 'Fitness for everyone — every budget, every body type, every interest.' },
    { icon: <Target size={32} color="#14B8A6" />, title: 'Value', desc: 'Smart spending on fitness. Every rupee should deliver a tangible benefit.' },
    { icon: <Heart size={32} color="#14B8A6" />, title: 'Fun', desc: 'Fitness should be enjoyable, not a chore. Variety, rewards, and community.' },
  ];

  return (
    <>
      <Helmet>
        <title>About HUSLRZ — Our Mission, Story & Values</title>
        <meta name="description" content="HUSLRZ is India's pay-per-use fitness marketplace. Born in Chennai, we're making fitness accessible to everyone — no memberships, no commitments." />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      </Helmet>

      <div style={{ fontFamily: 'Google Sans, Roboto, Arial, sans-serif', color: '#202124', minHeight: '100vh', background: '#ffffff' }}>
        {/* Navigation */}
        <nav style={styles.nav}>
          <div style={styles.navContainer}>
            {isMobile && <button style={styles.hamburger} onClick={() => setMobileMenuOpen(true)}><Menu size={24} color="#202124" /></button>}
            <Link to="/" style={styles.logo}>HUSLRZ</Link>
            {!isMobile && (
              <div style={styles.navLinks}>
                <Link to="/partners" style={styles.navLink}>For Partners</Link>
                <Link to="/corporate" style={styles.navLink}>For Corporates</Link>
                <Link to="/about" style={{ ...styles.navLink, color: '#14B8A6', fontWeight: 600 }}>About</Link>
                <Link to="/contact" style={styles.navLink}>Contact</Link>
                <Link to="/login" style={{ ...styles.navLink, color: '#14B8A6', fontWeight: 600 }}>Login</Link>
                <Link to="/signup" style={styles.primaryButton}>Get Started <ArrowRight size={16} /></Link>
              </div>
            )}
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMobile && mobileMenuOpen && (
          <>
            <div style={styles.mobileMenuOverlay} onClick={() => setMobileMenuOpen(false)} />
            <div style={styles.mobileMenu}>
              <button style={styles.closeButton} onClick={() => setMobileMenuOpen(false)}><X size={24} color="#202124" /></button>
              <Link to="/partners" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>For Partners</Link>
              <Link to="/corporate" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>For Corporates</Link>
              <Link to="/about" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0', color: '#14B8A6', fontWeight: 600 }} onClick={() => setMobileMenuOpen(false)}>About</Link>
              <Link to="/contact" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>Contact</Link>
              <Link to="/login" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0', color: '#14B8A6', fontWeight: 600 }} onClick={() => setMobileMenuOpen(false)}>Login</Link>
              <Link to="/signup" style={{ ...styles.primaryButton, width: '100%', justifyContent: 'center', padding: '16px 24px', fontSize: '16px', marginTop: '16px' }} onClick={() => setMobileMenuOpen(false)}>Get Started <ArrowRight size={18} /></Link>
            </div>
          </>
        )}

        {/* Hero */}
        <section style={{ padding: isMobile ? '48px 16px' : '80px 24px', textAlign: 'center' as const }}>
          <div style={styles.container}>
            <h1 style={{ fontSize: 'clamp(32px, 7vw, 56px)', fontWeight: 400, color: '#202124', marginBottom: '24px', lineHeight: 1.15, letterSpacing: '-0.5px' }}>
              Making fitness accessible<br />to <span style={{ color: '#14B8A6' }}>everyone</span>
            </h1>
            <p style={{ fontSize: isMobile ? '16px' : '20px', color: '#5f6368', maxWidth: '700px', margin: '0 auto', lineHeight: 1.6 }}>
              HUSLRZ is India's pay-per-use fitness marketplace. We believe fitness should be flexible, affordable, and fun — not locked behind annual memberships.
            </p>
          </div>
        </section>

        {/* Mission & Vision */}
        <section style={{ padding: isMobile ? '48px 16px' : '64px 24px', background: '#f8f9fa' }}>
          <div style={styles.container}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '32px' : '48px', maxWidth: '960px', margin: '0 auto' }}>
              <div>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#14B8A6', textTransform: 'uppercase' as const, letterSpacing: '1.5px', marginBottom: '12px' }}>Our Mission</div>
                <h2 style={{ fontSize: 'clamp(24px, 5vw, 32px)', fontWeight: 400, color: '#202124', marginBottom: '16px' }}>Fitness without commitment</h2>
                <p style={{ fontSize: '16px', color: '#5f6368', lineHeight: 1.8 }}>
                  To make fitness accessible to every Indian by removing the barriers of expensive memberships, long-term commitments, and limited choices. We connect people to the right activity, at the right place, at the right time — and they pay only for what they use.
                </p>
              </div>
              <div>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#14B8A6', textTransform: 'uppercase' as const, letterSpacing: '1.5px', marginBottom: '12px' }}>Our Vision</div>
                <h2 style={{ fontSize: 'clamp(24px, 5vw, 32px)', fontWeight: 400, color: '#202124', marginBottom: '16px' }}>A fitter India, one session at a time</h2>
                <p style={{ fontSize: '16px', color: '#5f6368', lineHeight: 1.8 }}>
                  To become India's default way to access fitness — where every gym, yoga studio, swimming pool, and turf is just a tap away. Where companies support employee wellness effortlessly. Where fitness facility owners thrive by filling every empty slot.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Story */}
        <section style={{ padding: isMobile ? '48px 16px' : '80px 24px', background: '#ffffff' }}>
          <div style={{ maxWidth: '760px', margin: '0 auto', padding: '0 24px' }}>
            <h2 style={{ fontSize: 'clamp(28px, 6vw, 40px)', fontWeight: 400, color: '#202124', marginBottom: '32px', textAlign: 'center' as const }}>
              Our Story
            </h2>
            <p style={{ fontSize: isMobile ? '16px' : '18px', color: '#5f6368', lineHeight: 1.8, marginBottom: '24px' }}>
              HUSLRZ was born from a frustration that millions of Indians share: gym memberships don't work for most people. You pay ₹15,000 upfront, go enthusiastically for two weeks, and then life gets in the way. The membership sits unused. The money is gone.
            </p>
            <p style={{ fontSize: isMobile ? '16px' : '18px', color: '#5f6368', lineHeight: 1.8, marginBottom: '24px' }}>
              We asked a simple question: <strong style={{ color: '#202124' }}>what if you could just pay for the days you actually go?</strong> What if you could try yoga on Monday, hit the gym on Wednesday, swim on Friday, and play badminton on Saturday — all from one app, with no commitment?
            </p>
            <p style={{ fontSize: isMobile ? '16px' : '18px', color: '#5f6368', lineHeight: 1.8, marginBottom: '24px' }}>
              At the same time, we saw that most gyms and fitness centres have 30-50% of their capacity sitting empty — especially during off-peak hours. These are slots that could be generating revenue but aren't.
            </p>
            <p style={{ fontSize: isMobile ? '16px' : '18px', color: '#5f6368', lineHeight: 1.8, marginBottom: '24px' }}>
              HUSLRZ bridges this gap. We connect fitness enthusiasts who want flexibility with facilities that have capacity. Users get freedom. Partners get revenue. Companies get a wellness program that employees actually use.
            </p>
            <p style={{ fontSize: isMobile ? '16px' : '18px', color: '#5f6368', lineHeight: 1.8 }}>
              Built in <strong style={{ color: '#202124' }}>Chennai</strong>, starting in South India, and expanding city by city — we're just getting started.
            </p>
          </div>
        </section>

        {/* Values */}
        <section style={{ padding: isMobile ? '48px 16px' : '80px 24px', background: '#f8f9fa' }}>
          <div style={styles.container}>
            <h2 style={{ fontSize: 'clamp(28px, 6vw, 40px)', fontWeight: 400, color: '#202124', marginBottom: '48px', textAlign: 'center' as const }}>
              What we stand for
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))', gap: isMobile ? '24px' : '32px', maxWidth: '1000px', margin: '0 auto' }}>
              {values.map((v, i) => (
                <div key={i} style={{ textAlign: 'center' as const }}>
                  <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>{v.icon}</div>
                  <h3 style={{ fontSize: '18px', fontWeight: 500, color: '#202124', marginBottom: '8px' }}>{v.title}</h3>
                  <p style={{ fontSize: '14px', color: '#5f6368', lineHeight: 1.7, margin: 0 }}>{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Location */}
        <section style={{ padding: isMobile ? '48px 16px' : '64px 24px', background: '#ffffff' }}>
          <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' as const }}>
            <MapPin size={32} color="#14B8A6" style={{ marginBottom: '16px' }} />
            <h2 style={{ fontSize: 'clamp(24px, 5vw, 32px)', fontWeight: 400, color: '#202124', marginBottom: '12px' }}>
              Based in Chennai
            </h2>
            <p style={{ fontSize: '16px', color: '#5f6368', lineHeight: 1.7, marginBottom: '8px' }}>
              HUSLRZ is headquartered in Chennai, Tamil Nadu, India. We're currently live in Chennai and Coimbatore, with plans to expand across South India and beyond.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section style={{ padding: isMobile ? '48px 16px' : '80px 24px', background: 'linear-gradient(135deg, #14B8A6 0%, #0891B2 100%)', textAlign: 'center' as const, color: '#ffffff' }}>
          <div style={styles.container}>
            <h2 style={{ fontSize: 'clamp(28px, 6vw, 44px)', fontWeight: 400, marginBottom: '16px' }}>
              Be part of the HUSLRZ story
            </h2>
            <p style={{ fontSize: isMobile ? '16px' : '18px', marginBottom: '40px', opacity: 0.95, maxWidth: '500px', margin: '0 auto 40px' }}>
              Whether you want to work out, list your facility, or offer wellness to your team — we'd love to have you.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/signup" style={{ background: '#ffffff', color: '#14B8A6', padding: '16px 32px', borderRadius: '24px', textDecoration: 'none', fontSize: '16px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                Get Started <ArrowRight size={18} />
              </Link>
              <Link to="/contact" style={{ background: 'transparent', color: '#ffffff', padding: '16px 32px', borderRadius: '24px', textDecoration: 'none', fontSize: '16px', fontWeight: 600, border: '2px solid #ffffff', display: 'inline-flex', alignItems: 'center' }}>
                Contact Us
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer style={{ background: '#111827', padding: isMobile ? '40px 16px' : '64px 24px', color: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: isMobile ? '32px' : '40px', marginBottom: '40px' }}>
              <div style={{ gridColumn: isMobile ? '1 / -1' : 'auto' }}>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#14B8A6', marginBottom: '12px' }}>HUSLRZ</div>
                <p style={{ fontSize: '14px', color: '#9CA3AF', lineHeight: 1.6, margin: 0 }}>India's pay-per-use fitness marketplace. Fitness without commitment.</p>
              </div>
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: '#ffffff' }}>Platform</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginBottom: '10px' }}><Link to="/partners" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>For Partners</Link></li>
                  <li style={{ marginBottom: '10px' }}><Link to="/corporate" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>For Corporates</Link></li>
                  <li style={{ marginBottom: '10px' }}><Link to="/about" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>About Us</Link></li>
                  <li><Link to="/contact" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Contact</Link></li>
                </ul>
              </div>
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: '#ffffff' }}>Legal</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginBottom: '10px' }}><Link to="/privacy" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Privacy Policy</Link></li>
                  <li style={{ marginBottom: '10px' }}><Link to="/terms" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Terms of Service</Link></li>
                  <li><Link to="/contact" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '14px' }}>Refund Policy</Link></li>
                </ul>
              </div>
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px', color: '#ffffff' }}>Get in Touch</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginBottom: '10px', fontSize: '14px', color: '#9CA3AF' }}>support@huslrz.com</li>
                  <li style={{ fontSize: '14px', color: '#9CA3AF' }}>Chennai, Tamil Nadu</li>
                </ul>
              </div>
            </div>
            <div style={{ borderTop: '1px solid #374151', paddingTop: '24px', textAlign: 'center' as const }}>
              <p style={{ fontSize: '13px', color: '#6B7280', margin: 0 }}>&copy; {new Date().getFullYear()} HUSLRZ. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default AboutPage;
